import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Droplet,
    Plus,
    Search,
    Filter,
    CheckCircle,
    Clock,
    Trash2,
    Eye,
    TrendingUp,
    Package,
    XCircle,
    Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import desmancheService from '@/services/desmanche.service';
import { fetchEdificios } from '@/services/edificios.service';
import RegistrarDesmancheModal from './components/RegistrarDesmancheModal';
import FinalizarDesmancheModal from './components/FinalizarDesmancheModal';
import DetalleDesmancheModal from './components/DetalleDesmancheModal';

const RopaEnDesmanchePage = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { user } = useAuth();

    const [registros, setRegistros] = useState([]);
    const [edificios, setEdificios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({});
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const limit = 10;

    const [showRegistrarModal, setShowRegistrarModal] = useState(false);
    const [showFinalizarModal, setShowFinalizarModal] = useState(false);
    const [showDetalleModal, setShowDetalleModal] = useState(false);
    const [selectedDesmanche, setSelectedDesmanche] = useState(null);

    const [indicadores, setIndicadores] = useState({
        totalPrendas: 0,
        totalRecuperadas: 0,
        totalNoRecuperadas: 0,
        totalEnProceso: 0,
    });
    const [loadingIndicadores, setLoadingIndicadores] = useState(false);

    useEffect(() => {
        loadEdificios();
        loadRegistros();
        loadIndicadores();
    }, [page, filters]);

    const loadEdificios = async () => {
        try {
            const response = await fetchEdificios({
                page: 1,
                limit: 100,
                query: {
                    tipoLogistica: 'vertical',
                    estado: 'Activo'
                }
            });
            setEdificios(response.data || []);
        } catch (error) {
            console.error('Error loading edificios:', error);
        }
    };

    const loadRegistros = async () => {
        setLoading(true);
        try {
            const queryFilters = {
                page,
                limit,
                ...filters,
            };

            if (searchTerm) queryFilters.tipoMancha = searchTerm;

            const response = await desmancheService.getDesmanche(queryFilters);
            setRegistros(response.data || []);
            setTotalRecords(response.totalRecords || 0);
        } catch (error) {
            console.error('Error loading registros:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar los registros de desmanche',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const loadIndicadores = async () => {
        setLoadingIndicadores(true);
        try {
            const queryFilters = {
                ...filters,
            };

            if (searchTerm) queryFilters.tipoMancha = searchTerm;

            const response = await desmancheService.getIndicadores(queryFilters);
            setIndicadores(response.data || {
                totalPrendas: 0,
                totalRecuperadas: 0,
                totalNoRecuperadas: 0,
                totalEnProceso: 0,
            });
        } catch (error) {
            console.error('Error loading indicadores:', error);
        } finally {
            setLoadingIndicadores(false);
        }
    };

    const handleRegistrarSuccess = () => {
        setShowRegistrarModal(false);
        loadRegistros();
        toast({
            title: 'Éxito',
            description: 'Proceso de desmanche registrado correctamente',
        });
    };

    const handleFinalizarSuccess = () => {
        setShowFinalizarModal(false);
        setSelectedDesmanche(null);
        loadRegistros();
        toast({
            title: 'Éxito',
            description: 'Proceso de desmanche finalizado correctamente',
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este registro?')) return;

        try {
            await desmancheService.deleteDesmanche(id);
            loadRegistros();
            toast({
                title: 'Éxito',
                description: 'Registro eliminado correctamente',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'No se pudo eliminar el registro',
                variant: 'destructive',
            });
        }
    };

    const handleSearch = () => {
        setPage(1);
        loadRegistros();
    };

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
        setIsFilterDrawerOpen(false);
    };

    const handleRemoveFilter = (filterKey) => {
        const newFilters = { ...filters };
        delete newFilters[filterKey];
        setFilters(newFilters);
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters({});
        setPage(1);
    };

    const filterFields = [
        {
            id: 'estado',
            label: 'Estado',
            type: 'select',
            options: [
                { value: 'En desmanche', label: 'En desmanche' },
                { value: 'Finalizado', label: 'Finalizado' },
            ],
        },
        {
            id: 'edificioId',
            label: 'Edificio',
            type: 'select',
            options: edificios.map((edificio) => ({
                value: edificio._id,
                label: edificio.nombre,
            })),
        },
        {
            id: 'tipoMancha',
            label: 'Tipo de Mancha',
            type: 'select',
            options: [
                { value: 'arrastre', label: 'Arrastre' },
                { value: 'amarilla', label: 'Amarilla' },
                { value: 'grasa', label: 'Grasa' },
                { value: 'tintas', label: 'Tintas' },
                { value: 'oxido', label: 'Óxido' },
                { value: 'clorhexidina', label: 'Clorhexidina' },
                { value: 'pegante', label: 'Pegante' },
                { value: 'azul_de_metileno', label: 'Azul de Metileno' },
                { value: 'otro', label: 'Otro' },
            ],
        },
    ];

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const calcularDias = (registro) => {
        if (registro.estado === 'Finalizado' && registro.dias) {
            return registro.dias;
        }
        
        const fechaRegistro = new Date(registro.fechaRegistro);
        const fechaActual = new Date();
        const diferenciaMilisegundos = fechaActual.getTime() - fechaRegistro.getTime();
        const dias = Math.ceil(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
        
        return dias;
    };

    const totalPages = Math.ceil(totalRecords / limit);

    return (
        <div className="container mx-auto p-4 ">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate('/lavanderia')}
                        className="h-10 w-10 shrink-0"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex items-center gap-4 flex-1">
                        <div className="bg-orange-50 p-3 rounded-lg shrink-0">
                            <Droplet className="h-8 w-8 text-orange-700" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                                Ropa en Desmanche
                            </h1>
                            <p className="text-gray-600 mt-1">
                                Registrar y gestionar procesos de desmanche de lencería
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsFilterDrawerOpen(true)}
                            className="shrink-0"
                        >
                            <Filter className="h-4 w-4 mr-2" />
                            Filtros
                            {Object.keys(filters).length > 0 && (
                                <Badge className="ml-2 bg-orange-600">
                                    {Object.keys(filters).length}
                                </Badge>
                            )}
                        </Button>
                        <ExportExcelButton
                            service={async () => {
                                const response = await desmancheService.getDesmanche({
                                    ...filters,
                                    limit: 10000,
                                    page: 1,
                                });
                                return response.data || [];
                            }}
                            filters={filters}
                            columns={[
                                { header: 'Edificio', key: 'edificio.nombre' },
                                { header: 'Ciudad', key: 'edificio.ciudad' },
                                { header: 'Estado', key: 'estado' },
                                { header: 'Tipo de Mancha', key: 'tipoMancha' },
                                { header: 'Total Prendas', key: 'totalPrendas' },
                                { header: 'Prendas Recuperadas', key: 'prendasRecuperadas' },
                                { header: 'Prendas No Recuperadas', key: 'prendasNoRecuperadas' },
                                { header: 'Días en Proceso', key: 'dias' },
                                { header: 'Fecha Registro', key: 'fechaRegistro' },
                                { header: 'Fecha Finalización', key: 'fechaFinalizacion' },
                                { header: 'Creador', key: 'creator.nombre' },
                                { header: 'Observaciones', key: 'observaciones' },
                            ]}
                            filename="registros_desmanche"
                        />
                        <Button
                            onClick={() => setShowRegistrarModal(true)}
                            className=""
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Registrar Desmanche
                        </Button>
                    </div>
                </div>
            </motion.div>

            {/* Indicadores */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 my-4"
            >
                {/* Total Prendas */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600">Total Prendas</p>
                                {loadingIndicadores ? (
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400 mt-2" />
                                ) : (
                                    <>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">
                                            {indicadores.totalPrendas.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">100%</p>
                                    </>
                                )}
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <Package className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Total Recuperadas */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600">Prendas Recuperadas</p>
                                {loadingIndicadores ? (
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400 mt-2" />
                                ) : (
                                    <>
                                        <p className="text-3xl font-bold text-green-600 mt-2">
                                            {indicadores.totalRecuperadas.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-green-600 font-medium mt-1">
                                            {indicadores.totalPrendas > 0
                                                ? ((indicadores.totalRecuperadas / indicadores.totalPrendas) * 100).toFixed(1)
                                                : 0}%
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg">
                                <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Total Sin Recuperar */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600">Sin Recuperar</p>
                                {loadingIndicadores ? (
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400 mt-2" />
                                ) : (
                                    <>
                                        <p className="text-3xl font-bold text-red-600 mt-2">
                                            {indicadores.totalNoRecuperadas.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-red-600 font-medium mt-1">
                                            {indicadores.totalPrendas > 0
                                                ? ((indicadores.totalNoRecuperadas / indicadores.totalPrendas) * 100).toFixed(1)
                                                : 0}%
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="bg-red-50 p-3 rounded-lg">
                                <XCircle className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Total En Proceso */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600">En Proceso</p>
                                {loadingIndicadores ? (
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400 mt-2" />
                                ) : (
                                    <>
                                        <p className="text-3xl font-bold text-orange-600 mt-2">
                                            {indicadores.totalEnProceso.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-orange-600 font-medium mt-1">
                                            {indicadores.totalPrendas > 0
                                                ? ((indicadores.totalEnProceso / indicadores.totalPrendas) * 100).toFixed(1)
                                                : 0}%
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="bg-orange-50 p-3 rounded-lg">
                                <Clock className="h-6 w-6 text-orange-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Applied Filters */}
            {Object.keys(filters).length > 0 && (
                <AppliedFilters
                    filters={filters}
                    fields={filterFields}
                    onRemoveFilter={handleRemoveFilter}
                    onClearFilters={handleClearFilters}
                />
            )}

            {/* Table */}
            <Card>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-8">Cargando...</div>
                    ) : registros.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No hay registros de desmanche
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Edificio
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Tipo de Mancha
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Total Prendas
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Fecha Registro
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Estado
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Días
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {registros.map((registro) => (
                                            <tr key={registro._id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    <div>
                                                        <div className="font-medium">
                                                            {registro.edificio.nombre}
                                                        </div>
                                                        <div className="text-gray-500 text-xs">
                                                            {registro.edificio.ciudad}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {registro.tipoMancha}
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium">
                                                    {registro.totalPrendas}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {formatDate(registro.fechaRegistro)}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <Badge
                                                        variant={
                                                            registro.estado === 'Finalizado'
                                                                ? 'success'
                                                                : 'warning'
                                                        }
                                                        className={
                                                            registro.estado === 'Finalizado'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-orange-100 text-orange-800'
                                                        }
                                                    >
                                                        {registro.estado === 'Finalizado' ? (
                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                        ) : (
                                                            <Clock className="h-3 w-3 mr-1" />
                                                        )}
                                                        {registro.estado}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <Badge variant="outline" className="font-semibold">
                                                        {calcularDias(registro)} {calcularDias(registro) === 1 ? 'día' : 'días'}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => {
                                                                setSelectedDesmanche(registro);
                                                                setShowDetalleModal(true);
                                                            }}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="destructive"
                                                            onClick={() => handleDelete(registro._id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                        {registro.estado === 'En desmanche' && (
                                                            <Button
                                                                size="sm"
                                                                className="bg-green-600 hover:bg-green-700"
                                                                onClick={() => {
                                                                    setSelectedDesmanche(registro);
                                                                    setShowFinalizarModal(true);
                                                                }}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-1" />
                                                                Finalizar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-gray-600">
                                    Mostrando {(page - 1) * limit + 1} a{' '}
                                    {Math.min(page * limit, totalRecords)} de {totalRecords}{' '}
                                    registros
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        Anterior
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                    >
                                        Siguiente
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Modals */}
            {showRegistrarModal && (
                <RegistrarDesmancheModal
                    open={showRegistrarModal}
                    onClose={() => setShowRegistrarModal(false)}
                    onSuccess={handleRegistrarSuccess}
                    edificios={edificios}
                    user={user}
                />
            )}

            {showFinalizarModal && selectedDesmanche && (
                <FinalizarDesmancheModal
                    open={showFinalizarModal}
                    onClose={() => {
                        setShowFinalizarModal(false);
                        setSelectedDesmanche(null);
                    }}
                    onSuccess={handleFinalizarSuccess}
                    desmanche={selectedDesmanche}
                />
            )}

            {showDetalleModal && selectedDesmanche && (
                <DetalleDesmancheModal
                    open={showDetalleModal}
                    onClose={() => {
                        setShowDetalleModal(false);
                        setSelectedDesmanche(null);
                    }}
                    desmanche={selectedDesmanche}
                />
            )}

            {/* Filter Drawer */}
            <FilterDrawer
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                fields={filterFields}
                initialFilters={filters}
                onApplyFilters={handleApplyFilters}
            />
        </div>
    );
};

export default RopaEnDesmanchePage;
